'use client'

import React from 'react'
import { createPortal } from 'react-dom'

type BottomSheetProps = React.HTMLAttributes<HTMLDivElement> & {
  isOpen: boolean
  onClose: () => void
  isCloseClickOutside?: boolean
}

const BottomSheetContext = React.createContext<{
  isOpen: boolean
  isCloseClickOutside: boolean
  onClose: () => void
}>({
  isOpen: false,
  isCloseClickOutside: true,
  onClose: () => {}
})

const BottomSheet = ({
  children,
  isOpen,
  onClose,
  isCloseClickOutside = true,
  className = '',
  ...props
}: BottomSheetProps) => {
  const contextValue = React.useMemo(
    () => ({ isOpen, onClose, isCloseClickOutside }),
    [isOpen, onClose, isCloseClickOutside]
  )
  const [isContentShown, setIsContentShown] = React.useState(false)

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 1000
    },
    content: {
      boxShadow: '0px 6px 16px 0px #3D47510F, 0px 0px 4px 1px #3D47510A',
      border: 'none',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      padding: '16px',
      top: 'auto',
      right: '0',
      left: '0',
      bottom: '0',
      marginLeft: 'auto',
      marginRight: 'auto',
      backgroundColor: 'FFF'
    }
  }

  const containerRef = React.useRef<HTMLDivElement>(null)

  const [animationClass, setAnimationClass] = React.useState('-mb-[100%]')

  React.useEffect(() => {
    // document.body.style.overflow = isOpen ? 'hidden' : 'auto'

    if (isOpen) {
      setIsContentShown(true)
      setTimeout(() => {
        setAnimationClass('-mb-0')
      }, 50)
    } else {
      setAnimationClass('-mb-[100%]')
      setTimeout(() => {
        setIsContentShown(false)
      }, 100)
    }
  }, [isOpen])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isCloseClickOutside
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, isCloseClickOutside])

  React.useEffect(() => {
    const bottomsheetOpen =
      document.querySelectorAll('.bottomsheet-open').length

    if (bottomsheetOpen > 0) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isContentShown])

  return (
    <BottomSheetContext.Provider value={contextValue}>
      {isContentShown &&
        createPortal(
          <div className="bg-black/40 inset-0 fixed z-[1000]">
            <div
              style={{
                ...customStyles.content
              }}
              ref={containerRef}
              className={`bg-white absolute left-0 right-0 bottom-auto mt-2 shadow-md border-0 p-2 transition-all duration-200 max-h-[90vh] min-w-full tablet-lg:min-w-[525px] ${animationClass} ${className} bottomsheet-open max-w-screen-tablet`}
              {...props}
            >
              {children}
            </div>
          </div>,
          document.body
        )}
    </BottomSheetContext.Provider>
  )
}

type BottomSheetHeaderProps = React.HTMLAttributes<HTMLDivElement>

const BottomSheetHeader = ({
  children,
  className = '',
  ...props
}: BottomSheetHeaderProps) => {
  const context = React.useContext(BottomSheetContext)

  if (!context) {
    throw new Error(
      'BottomSheetHeader must be used within a BottomSheet component'
    )
  }

  return (
    <div className={`flex flex-col gap-0 mb-6 ${className}`} {...props}>
      {children}
    </div>
  )
}

type BottomSheetTitleProps = React.HTMLAttributes<HTMLParagraphElement>

const BottomSheetTitle = ({
  children,
  className = '',
  ...props
}: BottomSheetTitleProps) => {
  const context = React.useContext(BottomSheetContext)

  if (!context) {
    throw new Error(
      'BottomSheetTitle must be used within a BottomSheet component'
    )
  }

  return (
    <p
      className={`heading-xl-bold text-neutral-gray-800 ${className}`}
      {...props}
    >
      {children}
    </p>
  )
}

const BottomSheetClose = () => {
  const context = React.useContext(BottomSheetContext)

  if (!context) {
    throw new Error(
      'BottomSheetClose must be used within a BottomSheet component'
    )
  }

  const { onClose } = context

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6 cursor-pointer text-neutral-gray-600 mr-0 ml-auto shrink-0"
      onClick={onClose}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  )
}

type BottomSheetContentProps = React.HTMLAttributes<HTMLDivElement>

const BottomSheetContent = ({
  children,
  className = '',
  ...props
}: BottomSheetContentProps) => {
  const context = React.useContext(BottomSheetContext)

  if (!context) {
    throw new Error(
      'BottomSheetContent must be used within a BottomSheet component'
    )
  }

  return (
    <div
      className={`overflow-y-auto max-h-[calc(90vh-120px)] ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

type BottomSheetActionsProps = React.HTMLAttributes<HTMLDivElement>

const BottomSheetActions = ({
  children,
  className = '',
  ...props
}: BottomSheetActionsProps) => (
  <div className={`mt-4 flex justify-end gap-4 ${className}`} {...props}>
    {children}
  </div>
)

BottomSheet.Header = BottomSheetHeader
BottomSheet.Title = BottomSheetTitle
BottomSheet.Close = BottomSheetClose
BottomSheet.Content = BottomSheetContent
BottomSheet.Actions = BottomSheetActions

export default BottomSheet
